import type { Schemas } from '@mrigl/api-client';
import { unwrap } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { Alert, SelectField, TextField } from '@mrigl/ui';
import { useParams } from 'react-router';
import { api } from '../api.js';
import { Loading } from '../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  type ListQuery,
} from '../grid.js';
import { useI18n } from '../i18n.js';

type AuditItem = Schemas['AuditLogPage']['items'][number];
type AuditQuery = ListQuery<'/admin-api/v1/stores/{storeId}/audit-events'>;
const ACTORS: readonly AuditItem['actorType'][] = ['user', 'system', 'api_client'];

/** Store audit trail (owners and admins): actor, action and date filters, pages. */
export function AuditLogPage() {
  const { storeId = '' } = useParams();
  const { t, formatDateTime } = useI18n();
  const grid = useGridState({
    sort: '-occurredAt',
    sortable: ['occurredAt'],
    filters: { actor: '', action: '', from: '', to: '' },
  });
  const { filters } = grid;
  const key = JSON.stringify([storeId, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<AuditItem>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/audit-events', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<AuditQuery['sort']>,
              ...(filters.actor === ''
                ? {}
                : { actorType: filters.actor as AuditItem['actorType'] }),
              ...(filters.action === '' ? {} : { action: filters.action }),
              ...(filters.from === '' ? {} : { from: filters.from }),
              ...(filters.to === '' ? {} : { to: filters.to }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  return (
    <div className="mrigl-stack">
      <h1 className="mrigl-heading">{t('admin.audit.title')}</h1>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.audit.filter.action')}
            value={filters.action}
            onChange={(value) => {
              grid.setFilter('action', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.audit.actor')}
          value={filters.actor}
          onChange={(event) => {
            grid.setFilter('actor', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.audit.filter.allActors') },
            ...ACTORS.map((value) => ({
              value,
              label: t(`admin.audit.actor.${value}` as MessageKey),
            })),
          ]}
        />
        <TextField
          type="date"
          label={t('admin.grid.from')}
          value={filters.from}
          max={filters.to === '' ? undefined : filters.to}
          onChange={(event) => {
            grid.setFilter('from', event.target.value);
          }}
        />
        <TextField
          type="date"
          label={t('admin.grid.to')}
          value={filters.to}
          min={filters.from === '' ? undefined : filters.from}
          onChange={(event) => {
            grid.setFilter('to', event.target.value);
          }}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.audit.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <SortHeader
                    field="occurredAt"
                    label={t('admin.audit.date')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.audit.actor')}</th>
                  <th scope="col">{t('admin.audit.action')}</th>
                  <th scope="col">{t('admin.audit.target')}</th>
                  <th scope="col">{t('admin.audit.ip')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <td className="admin-nowrap">{formatDateTime(item.occurredAt)}</td>
                    <td>{t(`admin.audit.actor.${item.actorType}` as MessageKey)}</td>
                    <td className="mrigl-ltr">
                      <code className="admin-code">{item.action}</code>
                    </td>
                    <td className="mrigl-ltr">{item.targetType}</td>
                    <td className="mrigl-ltr mrigl-muted">{item.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}
